import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { HintsToggle } from "@/components/ui/hints-toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InsightsDateRangeSelector } from "./insights-date-range-selector";

export interface SectionDef {
  id: string;
  label: string;
  group: string;
}

interface InsightsSectionNavProps {
  days?: number;
  onDaysChange?: (days: number) => void;
  showHints?: boolean;
  onShowHintsChange?: (checked: boolean) => void;
  sections?: SectionDef[];
  showDateRange?: boolean;
  dateLabel?: string;
}

const INSIGHT_SECTIONS: SectionDef[] = [
  {
    id: "section-capacity-utilization",
    label: "Capacity Utilization",
    group: "Capacity",
  },
  {
    id: "section-kennel-population",
    label: "Kennel Population",
    group: "Capacity",
  },
  {
    id: "section-population-breakdown",
    label: "Population Breakdown",
    group: "Capacity",
  },
  {
    id: "section-kennel-status-trends",
    label: "Kennel Status",
    group: "Capacity",
  },
  {
    id: "section-adoption-pipeline",
    label: "Adoption Levels",
    group: "Capacity",
  },
  {
    id: "section-intakes-outcomes",
    label: "Intakes & Outcomes",
    group: "Outcomes",
  },
  { id: "section-length-of-stay", label: "Length of Stay", group: "Outcomes" },
  {
    id: "section-average-stays",
    label: "Avg Stays per Animal",
    group: "Outcomes",
  },
  {
    id: "section-outcome-reasons",
    label: "Outcome Reasons",
    group: "Outcomes",
  },
  { id: "section-intake-sources", label: "Intake Sources", group: "Outcomes" },
];

function getScrollContainer(): HTMLElement | null {
  const marked = document.querySelector<HTMLElement>(
    "[data-insights-scroll-container]",
  );
  if (marked && marked.scrollHeight > marked.clientHeight) return marked;

  const main = document.querySelector<HTMLElement>("main");
  if (main && main.scrollHeight > main.clientHeight) return main;

  return marked ?? main ?? null;
}

export function InsightsSectionNav({
  days,
  onDaysChange,
  showHints,
  onShowHintsChange,
  sections: sectionsProp,
  showDateRange = true,
  dateLabel,
}: InsightsSectionNavProps) {
  const sections = sectionsProp ?? INSIGHT_SECTIONS;
  const [activeSection, setActiveSection] = useState<string>("");
  const navRef = useRef<HTMLElement>(null);
  const observedRef = useRef<Set<string>>(new Set());
  const pillRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const isClickScrolling = useRef(false);

  useEffect(() => {
    const navEl = navRef.current;
    if (!navEl) return;

    const scrollRoot = getScrollContainer();

    const visibleSections = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleSections.add(entry.target.id);
          } else {
            visibleSections.delete(entry.target.id);
          }
        }

        const topmost = sections.find((s) => visibleSections.has(s.id));
        if (topmost) {
          setActiveSection(topmost.id);
        }
      },
      {
        root: scrollRoot,
        rootMargin: "-64px 0px -20% 0px",
        threshold: [0, 0.1, 0.25],
      },
    );

    const handleScroll = () => {
      if (isClickScrolling.current) return;
      const container = scrollRoot || document.documentElement;
      const atBottom =
        Math.abs(
          container.scrollHeight - container.scrollTop - container.clientHeight,
        ) < 80;

      if (atBottom && sections.length > 0) {
        for (let i = sections.length - 1; i >= 0; i--) {
          const el = document.getElementById(sections[i].id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
              setActiveSection(sections[i].id);
              return;
            }
          }
        }
      }
    };

    const scrollTarget = scrollRoot || window;
    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });

    const tryObserve = () => {
      for (const section of sections) {
        if (observedRef.current.has(section.id)) continue;
        const el = document.getElementById(section.id);
        if (el) {
          observer.observe(el);
          observedRef.current.add(section.id);
        }
      }
    };

    tryObserve();

    const mutationObserver = new MutationObserver(() => {
      tryObserve();
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      scrollTarget.removeEventListener("scroll", handleScroll);
      observedRef.current.clear();
      visibleSections.clear();
    };
  }, [sections]);

  useEffect(() => {
    if (activeSection) {
      const pill = pillRefs.current.get(activeSection);
      if (pill) {
        const row = pill.closest("[data-nav-row]");
        if (row) {
          const pillRect = pill.getBoundingClientRect();
          const rowRect = row.getBoundingClientRect();
          const offset =
            pillRect.left -
            rowRect.left -
            rowRect.width / 2 +
            pillRect.width / 2;
          row.scrollBy({ left: offset, behavior: "smooth" });
        }
      }
    }
  }, [activeSection]);

  const handleClick = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    const navEl = navRef.current;
    if (!el || !navEl) return;

    setActiveSection(sectionId);
    isClickScrolling.current = true;

    const container = getScrollContainer();
    if (container) {
      const navHeight = navEl.offsetHeight || 48;
      let stickyOffset = navHeight + 8;

      const trendsControlIds = new Set([
        "section-capacity-utilization",
        "section-kennel-population",
        "section-population-breakdown",
        "section-kennel-status-trends",
        "section-adoption-pipeline",
      ]);
      if (trendsControlIds.has(sectionId)) {
        const controlsPanel = document.querySelector<HTMLElement>(
          "[data-testid='trends-shared-controls']",
        );
        if (controlsPanel) {
          stickyOffset += controlsPanel.offsetHeight + 8;
        }
      }

      const targetRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const scrollTarget =
        container.scrollTop +
        (targetRect.top - containerRect.top) -
        stickyOffset;
      container.scrollTo({
        top: Math.max(0, scrollTarget),
        behavior: "smooth",
      });
    }

    setTimeout(() => {
      isClickScrolling.current = false;
    }, 1200);
  }, []);

  const setPillRef = useCallback(
    (sectionId: string, el: HTMLButtonElement | null) => {
      if (el) {
        pillRefs.current.set(sectionId, el);
      } else {
        pillRefs.current.delete(sectionId);
      }
    },
    [],
  );

  const groups: { group: string; sections: SectionDef[] }[] = [];
  for (const section of sections) {
    const last = groups[groups.length - 1];
    if (last && last.group === section.group) {
      last.sections.push(section);
    } else {
      groups.push({ group: section.group, sections: [section] });
    }
  }

  const activeGroup = sections.find((s) => s.id === activeSection)?.group;

  return (
    <nav
      ref={navRef}
      className="sticky top-0 z-40 -mx-1 px-1 py-1.5 mb-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      data-testid="nav-insights-sections"
    >
      <div className="hidden sm:flex flex-col gap-0.5">
        {groups.map((g, gi) => (
          <div
            key={g.group}
            className="flex items-center gap-1"
            data-nav-row={g.group}
          >
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap mr-0.5 shrink-0">
              {g.group}:
            </span>
            {g.sections.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <Button
                  key={section.id}
                  ref={(el) => setPillRef(section.id, el)}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleClick(section.id)}
                  className={`text-xs whitespace-nowrap toggle-elevate ${isActive ? "toggle-elevated bg-primary/10 text-primary" : ""}`}
                  data-testid={`nav-pill-${section.id}`}
                >
                  {section.label}
                </Button>
              );
            })}
            {gi === 0 && (
              <div
                className="ml-auto flex items-center gap-3 shrink-0"
                data-testid="insights-header"
              >
                {showDateRange && days != null && onDaysChange ? (
                  <InsightsDateRangeSelector
                    days={days}
                    onDaysChange={onDaysChange}
                  />
                ) : dateLabel ? (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {dateLabel}
                  </span>
                ) : null}
                {onShowHintsChange != null && (
                  <HintsToggle
                    id="insights-show-hints"
                    checked={showHints ?? false}
                    onCheckedChange={onShowHintsChange}
                    label="Hints"
                    testId="switch-insights-hints"
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex sm:hidden flex-col gap-1.5">
        <div
          className="flex items-center gap-2"
          data-testid="insights-header-mobile"
        >
          {showDateRange && days != null && onDaysChange ? (
            <InsightsDateRangeSelector
              days={days}
              onDaysChange={onDaysChange}
            />
          ) : dateLabel ? (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {dateLabel}
            </span>
          ) : null}
          {onShowHintsChange != null && (
            <HintsToggle
              id="insights-show-hints-mobile"
              checked={showHints ?? false}
              onCheckedChange={onShowHintsChange}
              label="Hints"
              testId="switch-insights-hints-mobile"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {groups.map((g) => {
            const activeInGroup = g.sections.find(
              (s) => s.id === activeSection,
            );
            const displayValue = activeInGroup ? activeInGroup.id : "";
            return (
              <div key={g.group} className="flex-1 min-w-0">
                <Select
                  value={displayValue}
                  onValueChange={(val) => handleClick(val)}
                >
                  <SelectTrigger
                    className="w-full text-xs"
                    data-testid={`nav-select-${g.group.toLowerCase()}`}
                  >
                    <span className="text-muted-foreground mr-1 shrink-0">
                      {g.group}:
                    </span>
                    <SelectValue
                      placeholder={g.sections[0]?.label ?? g.group}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {g.sections.map((section) => (
                      <SelectItem key={section.id} value={section.id}>
                        {section.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
